import { useCallback } from "react";
import { useSelector } from "react-redux";

import { selectPermissions } from "@/app/authSlice";

/** Resource key → the page name an administrator would recognise. Mirrors
 *  core_auth/resources.py; a key missing here degrades to the raw key rather
 *  than costing a round-trip just to phrase a tooltip. */
const LABELS = {
  planning: "Planning Workbench",
  commit: "Plan commit",
  orders: "Production Orders",
  execution: "Schedule Monitor",
  variance: "Variance Analysis",
  reports: "Reports & analytics",
  master_data: "Products & Recipes",
  resources: "Equipment & Workforce",
  planning_rules: "Planning Rules",
  integrations: "Integrations",
  users: "Users & Roles",
  org: "Plants & Units",
  settings: "Scheduler Settings",
  audit: "Audit Log",
};

/**
 * What the signed-in role may do, from the grants delivered at sign-in.
 *
 * The older `useCanWrite`/`useIsAdmin` selectors answer from a hardcoded list
 * of role names, which cannot see the editable access grid: grant the Line
 * Manager the order book in Configuration and those hooks still say no. This
 * one reads the server's own answer, so the UI and the API agree.
 *
 * The grants arrive with the identity on /api/login and /api/me, so they are
 * already in the store before the first page renders — no fetch, no loading
 * state, no flicker from enabled to disabled.
 */
export function usePermission() {
  const role = useSelector((s) => s.auth.user?.role) ?? null;
  const grants = useSelector(selectPermissions);

  /**
   * `can("orders", "update")` — may the current role do this?
   *
   * Answers `true` when the grants have not arrived (an older token issued
   * before permissions rode along, say). A button that flickers from enabled
   * to disabled is worse than one briefly right for the wrong reason, and the
   * server refuses the write either way — this is a rendering courtesy, never
   * the control.
   */
  const can = useCallback(
    (resource, verb = "update") => {
      if (!role) return false;
      if (!grants) return true;
      return !!grants[resource]?.[verb];
    },
    [grants, role],
  );

  /** The sentence a disabled control shows, naming the role and the fix. */
  const why = useCallback(
    (resource, verb = "update") =>
      `Your role (${role ?? "not signed in"}) cannot ${verb} ` +
      `${LABELS[resource] ?? resource}. ` +
      "A Site Administrator can change this in Configuration → Access control.",
    [role],
  );

  return { can, why, role, ready: !!grants };
}
